import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Compass, PenTool, Code2, Rocket, Play, Landmark, ShoppingBag, LayoutDashboard, Smartphone, Building2 } from "lucide-react";

import first from "../Accets/1img.jpeg";
import sec from "../Accets/2img.jpeg";
import third from "../Accets/3img.jpeg";
import four from "../Accets/4img.jpeg";
import ecom from "../Accets/ecom.jpeg";
import saas from "../Accets/saas.jpeg";
import fin from "../Accets/fin.jpeg";
import video from "../Accets/car.mp4";

import {
  RevealSection,
  SplitText,
  MagneticButton,
  HorizontalScrollSection, // new
  staggerContainer,
  staggerItem,
  useParallax,
  EASE,
} from "../components/motion/ScrollFX";

// ---------------------------------------------------------------------
// Same visual system as Home.jsx, top to bottom: same theme tokens, same
// dark/light alternating bands, same constellation hero, same tab +
// spotlight interaction for "process", same masonry portfolio grid with
// mask-sweep hover, same CTA layout. Work keeps its own content (case
// studies, process steps) but is no longer a visually separate page.
// ---------------------------------------------------------------------
const theme = {
  ink: "#221619",
  inkMuted: "#6B6265",
  accent: "#E63946",
  accentDeep: "#B22234",
  gold: "#D9A441",
  border: "#E6DFDC",
  surface: "#FBF8F6",
  surfaceAlt: "#F2ECE9",
  heroBg: "#1A0E10",
  darkBand: "#221619",
  darkBandDeep: "#160B0D",
  ctaBg: "#2B1013",
  ctaBgDeep: "#160A0C",
};

const industries = [
  { no: "01", icon: Landmark, title: "Fintech", desc: "Secure payment flows, AEPS/KYC integrations and compliance-ready banking correspondent platforms." },
  { no: "02", icon: ShoppingBag, title: "E-Commerce", desc: "Storefronts, checkout flows and inventory systems built to convert and scale with demand." },
  { no: "03", icon: LayoutDashboard, title: "SaaS", desc: "Multi-tenant dashboards, subscription billing and usage analytics for growing software products." },
  { no: "04", icon: Smartphone, title: "Mobile Apps", desc: "Native-feeling Android & iOS experiences from a single, maintainable codebase." },
  { no: "05", icon: Building2, title: "Enterprise", desc: "Custom internal tools and workflow systems tailored to how a specific business actually operates." },
];

const heroWords = ["Fintech", "E-Commerce", "SaaS", "Mobile", "Enterprise"];
const heroEyebrow = "Case Studies & Projects";
const heroSubtitle =
  "Selected work across fintech, e-commerce and SaaS — real products we designed, built and scaled for real clients.";

const stepAccents = ["#E63946", "#D9A441", "#E63946", "#D9A441"];

const process = [
  {
    step: "01",
    title: "Discovery",
    icon: Compass,
    img: first,
    desc: "We start by understanding your business goals, your users, and the problem worth solving — before a single screen gets designed.",
  },
  {
    step: "02",
    title: "Design",
    icon: PenTool,
    img: sec,
    desc: "Wireframes become intuitive, conversion-focused interfaces — validated with real users, not just internal opinions.",
  },
  {
    step: "03",
    title: "Development",
    icon: Code2,
    img: third,
    desc: "Clean, scalable engineering on a modern stack, with security and performance built in from day one, not bolted on.",
  },
  {
    step: "04",
    title: "Launch & Scale",
    icon: Rocket,
    img: four,
    desc: "We deploy, monitor and keep improving — so the product that ships in month one still performs in year three.",
  },
];

const projects = [
  {
    title: "Fintech App",
    category: "Fintech",
    img: fin,
    link: "https://bankmudraa.com/Dashboard",
    external: true,
    size: "tall",
  },
  {
    title: "E-Commerce Platform",
    category: "E-Commerce",
    img: ecom,
    link: "https://unibcomp.in/",
    external: true,
    size: "short",
  },
  {
    title: "SaaS Dashboard",
    category: "SaaS",
    img: saas,
    link: "/Upcoming",
    external: false,
    size: "tall",
  },
];

const trustItems = ["Fintech", "E-Commerce", "SaaS", "Mobile Apps", "Enterprise", "Case Studies"];

// Reusable eyebrow — identical to Home's, so it reads as one product.
function Eyebrow({ children, center = false, light = false }) {
  return (
    <div
      className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase mb-4 ${
        center ? "mx-auto" : ""
      }`}
      style={{ color: light ? "#FF8A93" : theme.accent }}
    >
      <span className="h-px w-6" style={{ backgroundColor: light ? "#FF8A93" : theme.accent }} />
      {children}
    </div>
  );
}

function DotGrid({ className = "", light = false }) {
  return (
    <div
      className={`pointer-events-none absolute inset-0 ${light ? "opacity-[0.05]" : "opacity-[0.06]"} ${className}`}
      style={{
        backgroundImage: `radial-gradient(${light ? "#FFFFFF" : theme.ink} 1px, transparent 1px)`,
        backgroundSize: "26px 26px",
      }}
    />
  );
}

// Same rolling-word mechanic as Home's hero, sized off the longest word
// so nothing clips.
function RotatingWord({ words, interval = 2200 }) {
  const [index, setIndex] = useState(0);
  const maxChars = Math.max(...words.map((w) => w.length));

  useEffect(() => {
    const timer = setInterval(() => setIndex((p) => (p + 1) % words.length), interval);
    return () => clearInterval(timer);
  }, [words.length, interval]);

  return (
    <span
      className="relative inline-block align-bottom"
      style={{ height: "1.08em", width: `${maxChars + 1.5}ch`, overflowY: "hidden", overflowX: "visible" }}
    >
      <AnimatePresence mode="popLayout">
        <motion.span
          key={words[index]}
          initial={{ y: "60%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-60%", opacity: 0 }}
          transition={{ duration: 0.55, ease: [0.65, 0, 0.35, 1] }}
          className="absolute left-0 top-0 whitespace-nowrap"
          style={{ color: theme.accent }}
        >
          {words[index]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// Trust/category marquee — same infinite CSS-loop strip as Home.
function TrustBand() {
  return (
    <div
      className="relative py-8 overflow-hidden border-y"
      style={{ backgroundColor: theme.darkBandDeep, borderColor: "rgba(255,255,255,0.08)" }}
    >
      <style>{`
        @keyframes workMarquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .work-marquee-track { animation: workMarquee 22s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .work-marquee-track { animation: none; } }
      `}</style>
      <div className="flex w-max work-marquee-track">
        {[...trustItems, ...trustItems].map((item, i) => (
          <span key={i} className="px-10 text-lg font-bold whitespace-nowrap" style={{ color: "rgba(255,255,255,0.35)" }}>
            {item}
          </span>
        ))}
      </div>
    </div>
  );
}

// Same constellation network as Home's hero background — nodes drift,
// edges pulse — so both hero sections share one visual language.
// const networkNodes = [
//   { x: 8, y: 18 }, { x: 34, y: 8 }, { x: 58, y: 22 }, { x: 82, y: 10 },
//   { x: 20, y: 46 }, { x: 50, y: 52 }, { x: 76, y: 44 },
//   { x: 12, y: 78 }, { x: 42, y: 84 }, { x: 68, y: 76 }, { x: 90, y: 66 },
// ];
// const networkEdges = [
//   [0, 1], [1, 2], [2, 3], [1, 4], [2, 5], [3, 6], [4, 5], [5, 6],
//   [4, 7], [5, 8], [6, 9], [8, 9], [9, 10], [7, 8],
// ];

// New hero background — aurora blobs + sweeping beams + perspective grid,
// replaces the old node/edge constellation network.
function AuroraField() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <span className="hero-conic-halo" />
      <span className="aurora-blob aurora-blob-1" />
      <span className="aurora-blob aurora-blob-2" />
      <span className="aurora-blob aurora-blob-3" />
      <span className="hero-pulse-ring" />
      <span className="hero-grid-floor" />
      <span className="hero-beam hero-beam-1" />
      <span className="hero-beam hero-beam-2" />
      <span className="hero-beam hero-beam-3" />
      <span className="hero-noise" />

      {/* Drifting geometric outlines */}
      <span className="hero-shape hero-shape-1" />
      <span className="hero-shape hero-shape-2" />
      <span className="hero-shape hero-shape-3" />
      <span className="hero-shape hero-shape-4" />

      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
        {Array.from({ length: 18 }).map((_, i) => {
          const cx = (i * 37) % 100;
          const cy = (i * 53) % 100;
          const isGold = i % 3 === 0;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={isGold ? 0.4 : 0.25}
              fill={isGold ? "#D9A441" : "#FFFFFF"}
              className="aurora-particle"
              style={{ animationDelay: `${i * 0.4}s`, animationDuration: `${6 + (i % 5)}s` }}
            />
          );
        })}
      </svg>
    </div>
  );
}

// Home's DeviceStack, adapted to show the actual case-study video inside
// the browser frame instead of skeleton bars — same tilt-on-cursor
// interaction, same floating status chip pattern.
function ShowcaseStack() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 16 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 16 });

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const handleLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="relative w-full max-w-md mx-auto"
      style={{ perspective: 1200 }}
    >
      <div
        className="absolute -inset-10 rounded-[3rem] blur-3xl opacity-40"
        style={{ background: `radial-gradient(circle, ${theme.accent}, transparent 70%)` }}
      />

      <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="device-float relative rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          style={{ backgroundColor: "#241417" }}
        >
          <div className="flex items-center gap-1.5 px-4 py-3 border-b border-white/10">
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="w-2.5 h-2.5 rounded-full bg-white/20" />
            <span className="ml-3 text-[11px] text-white/40 font-mono">UniBcomp.work</span>
          </div>
          <div className="relative">
          <video
  src={video}
  autoPlay loop muted playsInline
  preload="auto"
  width={400}
  height={192}
  className="w-full h-48 object-cover"
/>
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A0E10]/70 via-transparent to-transparent" />
            <span className="absolute bottom-3 left-3 flex items-center gap-1.5 text-white/80 text-[11px] font-medium">
              <Play size={11} className="fill-white/80" /> Case study reel
            </span>
          </div>
          <div className="grid grid-cols-3 gap-3 p-5">
            {[
              { value: "150+", label: "Projects" },
              { value: "3", label: "Industries" },
              { value: "99.9%", label: "Uptime" },
            ].map((stat, i) => (
              <div key={i} className="h-14 rounded-lg px-2.5 py-2 flex flex-col justify-between" style={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                <span className="text-white text-xs font-bold leading-none">{stat.value}</span>
                <span className="text-white/40 text-[9px] leading-none">{stat.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, x: 20, y: -10 }}
        animate={{ opacity: 1, x: 0, y: 0 }}
        transition={{ delay: 0.9, duration: 0.6 }}
        className="absolute -right-4 top-8 rounded-xl px-4 py-3 shadow-xl border border-white/10 flex items-center gap-3"
        style={{ backgroundColor: "#2E1A1D" }}
      >
        <span className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
          <Rocket size={16} className="text-white" />
        </span>
        <div>
          <div className="text-white text-xs font-semibold">Shipped & scaling</div>
          <div className="text-white/40 text-[10px]">Live client products</div>
        </div>
      </motion.div>
    </div>
  );
}

// Add this new component near your other section components (e.g. above `export default function Work()`)
function StatBreaker() {
  const stats = [
    { value: "150+", label: "Projects shipped" },
    { value: "5", label: "Industries served" },
    { value: "99.9%", label: "Uptime average" },
    { value: "3x", label: "Avg. team velocity" },
  ];

  return (
    <section
      className="relative py-20 overflow-hidden"
      style={{ backgroundColor: theme.darkBandDeep }}
    >
      <DotGrid light />
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[36rem] h-[36rem] rounded-full blur-3xl opacity-[0.1]"
        style={{ backgroundColor: theme.gold }}
      />

      <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <h3 className="text-2xl md:text-4xl font-bold leading-snug text-white">
          <SplitText text="We don't just ship features —" />
          <br />
          <span style={{ color: theme.accent }}>
            <SplitText text="we ship outcomes clients can measure." delay={0.15} />
          </span>
        </h3>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {stats.map((s) => (
            <motion.div key={s.label} variants={staggerItem}>
              <div className="text-3xl md:text-4xl font-extrabold" style={{ color: theme.gold }}>
                {s.value}
              </div>
              <div className="mt-2 text-sm text-white/50">{s.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </RevealSection>
    </section>
  );
}

export default function Work() {
  const [activeStep, setActiveStep] = useState(0);
  const heroRef = useRef(null);
  const bgParallax = useParallax(heroRef, 50);
  const stackParallax = useParallax(heroRef, -24);

  return (
    <div className="w-full" style={{ backgroundColor: theme.surface }}>
      <style>{`
      /* Slowly rotating conic gradient halo behind everything */
.hero-conic-halo {
  position: absolute; top: 50%; left: 50%; width: 70rem; height: 70rem;
  margin: -35rem 0 0 -35rem; border-radius: 9999px;
  background: conic-gradient(from 0deg, ${theme.accent}22, transparent 20%, ${theme.gold}22 45%, transparent 65%, ${theme.accentDeep}22 85%, ${theme.accent}22 100%);
  filter: blur(60px);
  animation: haloRotate 40s linear infinite;
}
@keyframes haloRotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* Morphing blobs — shape + scale + position, not just scale */
.aurora-blob-1 {
  border-radius: 42% 58% 65% 35% / 45% 40% 60% 55%; /* set once, not animated */
  animation: auroraMorph1 18s ease-in-out infinite;
  will-change: transform;
}
.aurora-blob-2 { animation: auroraMorph2 24s ease-in-out infinite; border-radius: 60% 40% 30% 70% / 50% 60% 40% 50%; }
.aurora-blob-3 { animation: auroraMorph3 21s ease-in-out infinite; border-radius: 50% 50% 40% 60% / 60% 40% 60% 40%; }
@keyframes auroraMorph1 {
  0%, 100% { transform: translate(0,0) scale(1); }
  33% { transform: translate(8rem, 6rem) scale(1.2); }
  66% { transform: translate(4rem, -4rem) scale(0.9); }
}
@keyframes auroraMorph2 {
  0%, 100% { transform: translate(0, 0) scale(1); border-radius: 60% 40% 30% 70% / 50% 60% 40% 50%; }
  50% { transform: translate(-6rem, -8rem) scale(0.85) rotate(20deg); border-radius: 35% 65% 60% 40% / 40% 45% 55% 60%; }
}
@keyframes auroraMorph3 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-4rem, 5rem) scale(1.15) rotate(-15deg); border-radius: 60% 40% 55% 45% / 45% 55% 45% 55%; }
}

/* Pulsing glow ring, expands and fades like a radar sweep */
.hero-pulse-ring {
  position: absolute; top: 50%; left: 40%; width: 4px; height: 4px;
  border-radius: 9999px; border: 1px solid ${theme.accent};
  animation: pulseRing 4s ease-out infinite;
}
.hero-pulse-ring::before, .hero-pulse-ring::after {
  content: ""; position: absolute; inset: 0; border-radius: 9999px;
  border: 1px solid ${theme.gold};
}
.hero-pulse-ring::before { animation: pulseRing 4s ease-out infinite 1.3s; }
.hero-pulse-ring::after { animation: pulseRing 4s ease-out infinite 2.6s; }
@keyframes pulseRing {
  0% { width: 4px; height: 4px; opacity: 0.9; transform: translate(-50%, -50%); }
  100% { width: 28rem; height: 28rem; opacity: 0; transform: translate(-50%, -50%); }
}

/* Extra sweeping beam for more movement */
.hero-beam-3 { left: 45%; background: linear-gradient(to bottom, transparent, rgba(217,164,65,0.5), transparent); animation: beamSweep 13s ease-in-out infinite; }

/* Drifting geometric outline shapes for depth */
.hero-shape {
  position: absolute; border: 1px solid rgba(255,255,255,0.14);
  opacity: 0.5;
}
.hero-shape-1 {
  width: 90px; height: 90px; top: 12%; left: 6%; border-radius: 16px;
  animation: shapeFloat1 14s ease-in-out infinite;
}
.hero-shape-2 {
  width: 60px; height: 60px; bottom: 18%; left: 14%;
  border-radius: 9999px; border-color: rgba(230,57,70,0.3);
  animation: shapeFloat2 16s ease-in-out infinite;
}
.hero-shape-3 {
  width: 70px; height: 70px; top: 22%; right: 10%;
  transform: rotate(45deg); border-color: rgba(217,164,65,0.3);
  animation: shapeFloat3 18s ease-in-out infinite;
}
.hero-shape-4 {
  width: 40px; height: 40px; bottom: 10%; right: 20%; border-radius: 9999px;
  animation: shapeFloat1 12s ease-in-out infinite reverse;
}
@keyframes shapeFloat1 {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(1.5rem, -2rem) rotate(20deg); }
}
@keyframes shapeFloat2 {
  0%, 100% { transform: translate(0, 0) scale(1); }
  50% { transform: translate(-1.5rem, 1.5rem) scale(1.15); }
}
@keyframes shapeFloat3 {
  0%, 100% { transform: rotate(45deg) translate(0, 0); }
  50% { transform: rotate(75deg) translate(-1rem, 1.5rem); }
}

@media (prefers-reduced-motion: reduce) {
  .hero-conic-halo, .hero-pulse-ring, .hero-pulse-ring::before, .hero-pulse-ring::after,
  .hero-shape-1, .hero-shape-2, .hero-shape-3, .hero-shape-4, .hero-beam-3 {
    animation: none !important;
  }
}
      `}</style>

      {/* ================= HERO (dark, animated bg) ================= */}
      <section ref={heroRef} className="relative overflow-hidden pt-40 pb-24 md:pt-48 md:pb-32" style={{ backgroundColor: theme.heroBg }}>
        <motion.div style={{ y: bgParallax }} className="absolute inset-0">
  <AuroraField />
</motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
            <Eyebrow light>{heroEyebrow}</Eyebrow>
            <h1 className="text-4xl md:text-[3.4rem] font-extrabold leading-[1.08] tracking-tight text-white">
              <SplitText text="Work that drives" className="text-white" /> <RotatingWord words={heroWords} />
              <br />
              <SplitText text="real results" className="text-white" delay={0.25} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.55, ease: EASE }}
              className="mt-6 text-lg text-white/60 max-w-lg"
            >
              {heroSubtitle}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <MagneticButton>
                <Link
                  to="/contact"
                  data-cursor="hover"
                  style={{ backgroundColor: theme.accent }}
                  className="group inline-flex items-center gap-2 text-white px-7 py-3.5 rounded-md font-semibold hover:brightness-110 transition-all"
                >
                  Start a Project
                  <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
              <MagneticButton strength={0.25}>
                <Link
                  to="/services"
                  data-cursor="hover"
                  className="inline-flex items-center gap-2 border border-white/25 text-white px-7 py-3.5 rounded-md font-medium hover:bg-white hover:text-[#221619] transition-colors"
                >
                  Our Services
                </Link>
              </MagneticButton>
            </motion.div>
          </motion.div>

          <motion.div style={{ y: stackParallax }}>
            <ShowcaseStack />
          </motion.div>
        </div>
      </section>

      <TrustBand />

      {/* ================= PROCESS (dark band, spotlight tabs) ================= */}
      <section className="relative py-24 overflow-hidden" style={{ backgroundColor: theme.darkBand }}>
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-0 right-0 w-[32rem] h-[32rem] rounded-full blur-3xl opacity-[0.12] translate-x-1/3 -translate-y-1/3 transition-colors duration-500"
          style={{ backgroundColor: stepAccents[activeStep] }}
        />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <RevealSection className="max-w-2xl mb-16">
            <Eyebrow light>Our Process</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
              <SplitText text="How we take an idea to" />{" "}
              <span style={{ color: theme.accent }}>a shipped product</span>
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/60">
              Four stages, one team, zero handoff chaos — from first conversation to a product that keeps performing.
            </p>
          </RevealSection>

          <RevealSection delay={0.1} className="grid md:grid-cols-[280px_1fr] gap-4 md:gap-0 rounded-3xl border border-white/10 overflow-hidden">
            <div className="flex md:flex-col overflow-x-auto md:overflow-visible" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              {process.map((item, i) => {
                const isActive = i === activeStep;
                return (
                  <button
                    key={item.step}
                    data-cursor="hover"
                    onClick={() => setActiveStep(i)}
                    onMouseEnter={() => setActiveStep(i)}
                    className="relative flex items-center gap-3 text-left px-6 py-5 shrink-0 w-full transition-colors duration-300"
                    style={{ backgroundColor: isActive ? "rgba(255,255,255,0.05)" : "transparent" }}
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] transition-transform duration-300 origin-left"
                      style={{ backgroundColor: stepAccents[i], transform: isActive ? "scaleX(1)" : "scaleX(0)" }}
                    />
                    <span className="font-mono text-xs font-semibold transition-colors duration-300" style={{ color: isActive ? stepAccents[i] : "rgba(255,255,255,0.35)" }}>
                      {item.step}
                    </span>
                    <span className="text-sm font-semibold whitespace-nowrap transition-colors duration-300" style={{ color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.55)" }}>
                      {item.title}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="relative min-h-[320px]" style={{ backgroundColor: "rgba(255,255,255,0.02)" }}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="grid sm:grid-cols-[1fr_1.1fr] h-full"
                >
                  <div className="p-10 md:p-14 flex flex-col justify-center">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                      style={{ backgroundColor: `${stepAccents[activeStep]}26`, color: stepAccents[activeStep] }}
                    >
                      {(() => {
                        const ActiveIcon = process[activeStep].icon;
                        return <ActiveIcon size={26} />;
                      })()}
                    </div>
                    <h3 className="text-2xl md:text-3xl font-bold text-white">{process[activeStep].title}</h3>
                    <p className="mt-4 text-white/60 leading-relaxed">{process[activeStep].desc}</p>
                  </div>
                  <div className="img-mask group relative overflow-hidden hidden sm:block">
                    <img src={process[activeStep].img} alt={process[activeStep].title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0" style={{ background: "linear-gradient(115deg, rgba(26,14,16,0.55), transparent 60%)" }} />
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealSection>
        </div>
      </section>
      {/* ================= INDUSTRIES (horizontal pinned scroll, dark) ================= */}
<section className="relative py-24 md:py-0" style={{ backgroundColor: theme.darkBandDeep }}>
  <div className="max-w-7xl mx-auto px-6 md:pt-24">
    <RevealSection className="max-w-2xl mb-16">
      <Eyebrow light>Where We've Delivered</Eyebrow>
      <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
        <SplitText text="Industries we build" /> <SplitText text="for" delay={0.2} />
      </h2>
    </RevealSection>
  </div>

  <div className="md:h-screen">
    <HorizontalScrollSection
      className="px-6 md:h-full md:flex md:items-center"
      items={industries}
      accent={theme.accent}
      renderCard={(item) => (
        <div
          data-cursor="hover"
          className="group relative rounded-3xl border p-8 h-full min-h-[280px] flex flex-col justify-between overflow-hidden"
          style={{ backgroundColor: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.1)" }}
        >
          <span className="card-glow-dark" />
          <div className="relative flex items-center justify-between">
            <span
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: "rgba(230,57,70,0.18)", color: "#FF8A93" }}
            >
              <item.icon size={22} />
            </span>
            <span className="font-mono text-sm font-semibold" style={{ color: theme.accent }}>
              {item.no}
            </span>
          </div>
          <div className="relative">
            <h3 className="text-2xl font-bold text-white">{item.title}</h3>
            <p className="mt-3 text-white/60 leading-relaxed">{item.desc}</p>
          </div>
        </div>
      )}
    />
  </div>
</section>
<StatBreaker />

      {/* ================= FEATURED PROJECTS (masonry, light) ================= */}
      <section className="relative py-24" style={{ backgroundColor: theme.surface }}>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <RevealSection>
            <Eyebrow center>Selected Work</Eyebrow>
            <h2 className="text-4xl md:text-5xl font-extrabold" style={{ color: theme.ink }}>
              <SplitText text="Featured" /> <span style={{ color: theme.accent }}>Projects</span>
            </h2>
            <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: theme.inkMuted }}>
              A few of the products we've helped design, build and scale into real, working businesses.
            </p>
          </RevealSection>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            className="mt-16 grid sm:grid-cols-2 md:grid-cols-3 gap-6 text-left"
          >
            {projects.map((project, i) => (
              <motion.a
                href={project.link}
                target={project.external ? "_blank" : undefined}
                rel={project.external ? "noopener noreferrer" : undefined}
                variants={staggerItem}
                whileHover={{ y: -10, rotate: i % 2 === 0 ? -0.8 : 0.8, scale: 1.015 }}
                transition={{ type: "spring", stiffness: 240, damping: 18 }}
                key={project.title}
                data-cursor="hover"
                className={`group relative block overflow-hidden rounded-2xl border shadow-sm hover:shadow-2xl ${
                  project.size === "tall" ? "md:mt-0" : "md:mt-10"
                }`}
                style={{ backgroundColor: theme.surface, borderColor: theme.border }}
              >
                <div className="img-mask relative overflow-hidden">
                  <img
                     src={project.img}
  width={640}
  height={project.size === "tall" ? 288 : 192}
  loading="lazy"
  decoding="async"
                    className={`w-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-transform duration-500 ${
                      project.size === "tall" ? "h-72" : "h-48"
                    }`}
                    style={{ backgroundColor: theme.surfaceAlt }}
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(34,22,25,0.75), transparent 55%)" }} />
                  <div className="portfolio-overlay absolute bottom-4 left-5 right-5 flex items-center justify-between">
                    <span className="text-white text-sm font-semibold">
                      {project.external ? "Visit Live Site" : "Coming Soon"}
                    </span>
                    <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: theme.accent }}>
                      <ArrowUpRight size={15} className="text-white" />
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <span className="text-xs font-semibold tracking-wide uppercase" style={{ color: theme.accent }}>
                    {project.category}
                  </span>
                  <h3 className="mt-1 text-lg font-semibold" style={{ color: theme.ink }}>
                    {project.title}
                  </h3>
                </div>
              </motion.a>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= CTA (dark) ================= */}
      <section
        className="relative py-28 overflow-hidden"
        style={{ background: `linear-gradient(160deg, ${theme.ctaBg} 0%, ${theme.ctaBgDeep} 100%)` }}
      >
        <DotGrid light />
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40rem] h-[40rem] rounded-full blur-3xl opacity-[0.14]"
          style={{ backgroundColor: theme.accent }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none select-none absolute -left-6 -top-10 text-[14rem] font-extrabold leading-none"
          style={{ color: "rgba(255,255,255,0.04)" }}
        >
          {"</>"}
        </span>

        <RevealSection className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Eyebrow center light>Let's Talk</Eyebrow>
          <h2 className="text-4xl md:text-5xl font-extrabold leading-tight text-white">
            <SplitText text="Have a project" />
            <span className="block">
              <SplitText text="in mind?" delay={0.15} />
            </span>
          </h2>
          <p className="mt-6 text-lg text-white/60 max-w-xl mx-auto">
            Tell us where you're stuck or where you want to go — we'll tell you honestly how we'd build it.
          </p>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <MagneticButton>
              <Link
                to="/contact"
                data-cursor="hover"
                style={{ backgroundColor: theme.accent }}
                className="group inline-flex items-center gap-2 px-9 py-4 rounded-md text-white font-semibold text-lg shadow-lg shadow-black/30 hover:brightness-110 transition-all"
              >
                Start a Project
                <ArrowRight size={20} className="transition-transform group-hover:translate-x-1" />
              </Link>
            </MagneticButton>
            <MagneticButton strength={0.25}>
              <Link
                to="/"
                data-cursor="hover"
                className="inline-flex items-center gap-2 border border-white/20 text-white px-9 py-4 rounded-md font-medium text-lg hover:bg-white hover:text-[#221619] transition-colors"
              >
                Back to Home
              </Link>
            </MagneticButton>
          </div>
        </RevealSection>
      </section>
    </div>
  );
}